import { FC } from 'react';
import { Star, Quote } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Avatar } from '../../ui/Avatar';
import { cn } from '../../../utils/cn';

export interface TestimonialCardProps {
  /**
   * The testimonial quote text
   */
  quote: string;

  /**
   * Client's name
   */
  clientName: string;

  /**
   * Star rating (1-5)
   */
  rating: number;

  /**
   * Optional service received
   */
  service?: string;

  /**
   * Optional avatar image URL
   */
  avatarSrc?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Renders a row of star icons based on the rating
 */
const StarRating: FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          className={cn(
            'h-4 w-4',
            index < rating
              ? 'fill-amber-400 text-amber-400'
              : 'fill-gray-200 text-gray-200 dark:fill-slate-600 dark:text-slate-600'
          )}
          aria-hidden="true"
        />
      ))}
    </div>
  );
};

export const TestimonialCard: FC<TestimonialCardProps> = ({
  quote,
  clientName,
  rating,
  service,
  avatarSrc,
  className,
}) => {
  return (
    <Card
      padding="lg"
      className={cn('relative flex min-w-[300px] flex-col sm:min-w-[340px]', className)}
    >
      {/* Decorative quote mark */}
      <Quote
        className="absolute right-4 top-4 h-8 w-8 text-secondary-200 dark:text-secondary-700"
        aria-hidden="true"
      />

      {/* Star rating */}
      <div className="mb-4">
        <StarRating rating={rating} />
      </div>

      {/* Quote text */}
      <blockquote className="mb-6 flex-grow">
        <p className="text-gray-700 dark:text-slate-300 leading-relaxed">"{quote}"</p>
      </blockquote>

      {/* Client info */}
      <div className="flex items-center gap-3 border-t border-gray-100 dark:border-slate-700 pt-4">
        <Avatar name={clientName} src={avatarSrc} size="md" className="flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-gray-900 dark:text-slate-100 truncate">{clientName}</p>
          {service && (
            <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{service}</p>
          )}
        </div>
      </div>
    </Card>
  );
};

TestimonialCard.displayName = 'TestimonialCard';
